import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import ShopifyBuyButton from '../components/ShopifyBuyButton';
import { useShopifyProduct } from '../hooks/useShopifyProducts';
import { handleImageError } from '../utils/imageOptimization';

// Convert "THE BAG" to "The Bag"
const toSentenceCase = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Accordion component
const Accordion = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-plumo-black/10">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4"
        style={{
          fontFamily: "'Space Mono', monospace",
          fontSize: '12px',
          letterSpacing: '0.05em',
          color: '#1A1814',
        }}
      >
        <span>{title}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-plumo-black/40" />
        ) : (
          <ChevronDown className="w-4 h-4 text-plumo-black/40" />
        )}
      </button>
      {isOpen && (
        <div 
          className="pb-4"
          style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: '12px',
            color: 'rgba(26,24,20,0.6)',
            lineHeight: 1.7,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const Product = () => {
  const { id } = useParams();
  const [selectedImage, setSelectedImage] = useState(0);
  
  // Fetch product data directly from Shopify
  const { product, loading, error } = useShopifyProduct(id);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-4 bg-plumo-bone w-24 mb-12" />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
              <div className="lg:col-span-3 aspect-[4/5] bg-plumo-bone" />
              <div className="lg:col-span-2 space-y-6 lg:pt-20">
                <div className="h-8 bg-plumo-bone w-3/4" />
                <div className="h-5 bg-plumo-bone w-1/4" />
                <div className="h-20 bg-plumo-bone" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              fontStyle: 'italic',
              fontSize: '32px',
              color: '#1A1814',
              marginBottom: '24px',
            }}
          >
            Product not found
          </h1>
          <Link 
            to="/shop" 
            className="font-body text-sm text-plumo-black/60 hover:text-plumo-black transition-colors underline underline-offset-4"
          >
            back to shop
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0 ? product.images : [product.image];
  
  // Strip descriptors from product name
  let shortName = product.name;
  if (shortName.includes(' — ')) {
    shortName = shortName.split(' — ')[0].trim();
  } else if (shortName.includes(' - ')) {
    shortName = shortName.split(' - ')[0].trim();
  }

  // Use Shopify prices directly - no hardcoded values
  const salePrice = Math.round(product.price);
  const compareAtPrice = product.compareAtPrice ? Math.round(product.compareAtPrice) : null;
  const hasSale = compareAtPrice && compareAtPrice > salePrice;

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 mb-8"
          style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: '11px',
            letterSpacing: '0.1em',
            color: 'rgba(26,24,20,0.5)',
          }}
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-3 h-3" />
          back
        </Link>

        {/* Main Content - Nomès style layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16">
          {/* LEFT COLUMN (60% = 3/5) - Large product image */}
          <div className="lg:col-span-3">
            <div className="aspect-[4/5] bg-plumo-bone overflow-hidden">
              <img
                src={images[selectedImage]}
                alt={product.name}
                width="1000"
                height="1250"
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
                onError={handleImageError}
                data-testid="product-main-image"
              />
            </div>
            
            {/* Thumbnail selector on desktop */}
            {images.length > 1 && (
              <div className="hidden lg:flex gap-3 mt-4">
                {images.slice(0, 5).map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-16 h-20 overflow-hidden transition-opacity ${
                      selectedImage === index ? 'opacity-100' : 'opacity-40 hover:opacity-70'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} view ${index + 1}`}
                      width="64"
                      height="80"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={handleImageError}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN (40% = 2/5) - Product info, starts ~80px from top */}
          <div className="lg:col-span-2 lg:pt-20">
            {/* Product Name */}
            <h1 
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: 'italic',
                fontSize: '32px',
                color: '#1A1814',
                lineHeight: 1.1,
                marginBottom: '16px',
              }}
              data-testid="product-name"
            >
              {toSentenceCase(shortName)}
            </h1>

            {/* Price - Inline strikethrough + sale */}
            <div 
              className="mb-2"
              data-testid="product-price"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: 'italic',
                fontSize: '18px',
              }}
            >
              {hasSale && (
                <span 
                  style={{
                    color: 'rgba(26,24,20,0.4)',
                    textDecoration: 'line-through',
                    marginRight: '12px',
                  }}
                >
                  ${compareAtPrice}
                </span>
              )}
              <span style={{ color: '#1A1814' }}>
                ${salePrice}
              </span>
            </div>

            {/* Tax line */}
            <p 
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '11px',
                color: 'rgba(26,24,20,0.5)',
                marginBottom: '24px',
              }}
            >
              tax included.
            </p>

            {/* Material / Size / Details */}
            <div 
              className="mb-6 space-y-2"
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '13px',
                lineHeight: 1.7,
              }}
            >
              <p>
                <span style={{ color: '#1A1814' }}>material: </span>
                <span style={{ color: 'rgba(26,24,20,0.7)' }}>18k gold-plated brass</span>
              </p>
              <p>
                <span style={{ color: '#1A1814' }}>finish: </span>
                <span style={{ color: 'rgba(26,24,20,0.7)' }}>polished</span>
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-plumo-black/10 my-6" />

            {/* Shopify Buy Button (includes variant selector and add to cart) */}
            <ShopifyBuyButton productId={id} />

            {/* Below button text */}
            <p 
              className="mt-6"
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '12px',
                color: 'rgba(26,24,20,0.5)',
                lineHeight: 1.7,
              }}
            >
              each piece carefully chosen. free 30-day returns. ships worldwide.
            </p>

            {/* Accordion sections */}
            <div className="mt-8">
              <Accordion title="reviews" defaultOpen={false}>
                <p>★★★★★ 4.9 average rating</p>
                <p className="mt-2">"exactly like the photos. i've gotten so many compliments already." — H.</p>
              </Accordion>
              
              <Accordion title="shipping" defaultOpen={false}>
                <p>all orders ship within 2-3 business days.</p>
                <p className="mt-2">free shipping on orders over $50.</p>
                <p>standard delivery 5-7 business days.</p>
              </Accordion>
              
              <Accordion title="care" defaultOpen={false}>
                <p>store in a cool, dry place away from direct sunlight.</p>
                <p className="mt-2">avoid contact with water, perfume, and lotions.</p>
                <p>polish gently with a soft cloth.</p>
              </Accordion>
              
              <Accordion title="sizing" defaultOpen={false}>
                <p>one size fits most.</p>
                <p className="mt-2">adjustable pieces noted in description.</p>
              </Accordion>
            </div>
          </div>
        </div>

        {/* Additional Images - Full width below, stacked vertically */}
        {images.length > 1 && (
          <div className="mt-24 space-y-4">
            {images.slice(1, 5).map((img, index) => (
              <div key={index} className="w-full">
                <img
                  src={img}
                  alt={`${product.name} detail ${index + 2}`}
                  width="1400"
                  height="1750"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                  onError={handleImageError}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Product;
